import React, { useEffect } from "react";
import { Highlight } from "@multiagentbook/ui";
import { AlertTriangle } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

// Styled link component for use inside Trans - combines Highlight styling with <a> tag
// Note: Nesting <Highlight><a /></Highlight> inside Trans components prop doesn't work
// because Trans flattens nested components. Use a single element instead.
const HighlightLink = (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a {...props} className={`border-accent border-b ${props.className || ''}`} />
);

export const Faqs = () => {
  const { t } = useTranslation("home");

  // Function to create URL-friendly slugs from questions
  const createSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .trim();
  };

  // Handle scrolling on component mount if there's a hash
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, []);

  // Using Trans component to embed links in translation strings
  // Translation strings use tags like <site>text</site> which get replaced by components
  const faqs = [
    {
      questionKey: "futureUpdates",
      question: t("faq.questions.futureUpdates.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.futureUpdates.answerTrans"
          ns="home"
          components={{
            site: <HighlightLink href="https://buy.multiagentbook.com" />,
            bold: <span className="font-semibold" />,
            paperback: <HighlightLink href="https://www.amazon.com/dp/B0G2BCQQJY" target="_blank" rel="noreferrer" />,
            hardcover: <HighlightLink href="https://www.amazon.com/dp/B0G2F6T2BZ" target="_blank" rel="noreferrer" />,
          }}
        />
      ),
    },
    {
      questionKey: "freeContent",
      question: t("faq.questions.freeContent.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.freeContent.answerTrans"
          ns="home"
          components={{
            newsletter: <HighlightLink href="https://newsletter.victordibia.com/" target="_blank" rel="noreferrer" />,
            uxDesign: <HighlightLink href="https://newsletter.victordibia.com/p/4-ux-design-principles-for-multi" target="_blank" rel="noreferrer" />,
            overviews: <HighlightLink href="https://newsletter.victordibia.com/p/multi-agent-llm-applications-a-review" target="_blank" rel="noreferrer" />,
            comparisons: <HighlightLink href="https://newsletter.victordibia.com/p/10-ways-to-compare-and-select-the" target="_blank" rel="noreferrer" />,
            strong: <strong />,
            highlight: <Highlight />,
          }}
        />
      ),
    },
    {
      questionKey: "frameworkSpecific",
      question: t("faq.questions.frameworkSpecific.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.frameworkSpecific.answerTrans"
          ns="home"
          components={{
            highlight: <Highlight />,
            picoagents: <a href="https://github.com/victordibia/designing-multiagent-systems/tree/main/picoagents" target="_blank" rel="noreferrer" className="text-accent hover:underline font-semibold" />,
          }}
        />
      ),
    },
    {
      questionKey: "multipleModels",
      question: t("faq.questions.multipleModels.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.multipleModels.answerTrans"
          ns="home"
          components={{
            highlight: <Highlight />,
          }}
        />
      ),
    },
    {
      questionKey: "githubRepo",
      question: t("faq.questions.githubRepo.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.githubRepo.answerTrans"
          ns="home"
          components={{
            repo: <HighlightLink href="https://github.com/victordibia/designing-multiagent-systems" target="_blank" rel="noreferrer" />,
            highlight: <Highlight />,
          }}
        />
      ),
    },
    {
      questionKey: "bookCover",
      question: t("faq.questions.bookCover.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.bookCover.answerTrans"
          ns="home"
          components={{
            highlight: <Highlight />,
            strong: <strong />,
            outline: <HighlightLink href="/book" className="text-accent" />,
          }}
        />
      ),
    },
    {
      questionKey: "targetAudience",
      question: t("faq.questions.targetAudience.question"),
      answer: (
        <>
          <Trans
            i18nKey="faq.questions.targetAudience.answerTrans"
            ns="home"
            components={{
              strong: <strong />,
              li: <li />,
              ul: <ul className="list-disc list-inside" />,
              p: <p />,
            }}
          />
          <div className="mt-2 p-2 rounded border border-secondary">
            <AlertTriangle className="w-4 -mt-1 text-accent inline-block mr-1" />{" "}
            {t("faq.questions.targetAudience.note")}
          </div>
        </>
      ),
    },
    {
      questionKey: "upgrade",
      question: t("faq.questions.upgrade.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.upgrade.answerTrans"
          ns="home"
          components={{
            bold: <span className="font-semibold" />,
            site: <HighlightLink href="https://buy.multiagentbook.com" />,
          }}
        />
      ),
    },
    {
      questionKey: "feedback",
      question: t("faq.questions.feedback.question"),
      answer: (
        <Trans
          i18nKey="faq.questions.feedback.answerTrans"
          ns="home"
          components={{
            issues: <HighlightLink href="https://github.com/victordibia/designing-multiagent-systems/issues" target="_blank" rel="noreferrer" />,
          }}
        />
      ),
    },
  ];

  return (
    <div className="border-t border-secondary mt-20 pt-20">
      <div className="bg-primary">
        <div className="mx-auto max-w-7xl    pb-12   ">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-5">
              <h2 className="text-3xl font-bold tracking-tight  sm:text-4xl">
                {t("faq.title")}
              </h2>
              <p className="mt-4 text-base leading-7 text-secondary">
                {t("faq.cantFindAnswer")}{" "}
                <Highlight>
                  <a
                    href="https://github.com/victordibia/designing-multiagent-systems/issues"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("faq.githubRepo")}
                  </a>
                </Highlight>
              </p>
            </div>
            <div className="mt-10 lg:col-span-7 lg:mt-0">
              <dl className="space-y-10">
                {faqs.map((faq) => {
                  const slug = createSlug(faq.question);
                  return (
                    <div key={faq.questionKey} id={slug}>
                      <dt className="text-xl font-semibold leading-7 text-primary">
                        {faq.question}
                      </dt>
                      <dd className="mt-2 text-lg leading-7 text-primary">
                        {faq.answer}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faqs;
